import { notifyForbidden } from '../lib/notifyForbidden'

export type EimSite = 'gti' | 'jm_baxi'

export interface EimVehicle {
  site: EimSite
  vehicle_id: string
  polled_ts: string
  soc: number | null
  batt_volt: number | null
  batt_cur: number | null
  max_cell_temp: number | null
  min_cell_temp: number | null
  motor_temp: number | null
  odometer: number | null
  gps_speed: number | null
  lat: number | null
  lng: number | null
  heading: number | null
  /**
   * 'parked' is a stationary vehicle whose CURRENT idle stretch has drawn essentially no
   * power the whole time it's been measured -- see EIM_PARKED_POWER_FLOOR_W's comment in
   * serving/app.py. Split out from 'idle' because they mean different things to ops: a
   * truck idling and drawing power is (possibly wastefully) running HVAC/aux systems; a
   * truck reporting gps_speed 0 and ~0W for hours is simply switched off. Confirmed live:
   * a GTI truck sat at gps_speed 0 for 798 minutes across 1641 straight polls with
   * avg_power_w exactly 0 the entire time, while every genuinely power-drawing idle
   * episode in the same fleet measured at least 76W -- reporting the first case as
   * "idling 798 minutes" was a false alarm, not a real idle/HVAC finding.
   */
  status: 'idle' | 'moving' | 'charging' | 'parked'
  /** Set only while `status === 'idle'` and the idle stretch is still open. Never set
   *  while charging or parked -- see IDLE_ALERT_THRESHOLD_MIN's own comment on why. */
  idle_since: string | null
  /** Average draw over the CURRENT idle episode -- safe to use directly because
   *  f_eim_idle_episodes() (db/reports_eim.sql) splits an episode the moment power
   *  crosses the parked floor, not just on a time gap. Before that fix, a truck that
   *  drew real power once and then went fully dead could keep one old event averaged
   *  into an ever-growing episode for hours; the episode boundary is what's fixed now,
   *  not this field. */
  idle_power_w: number | null
  /** Set only while the vehicle's latest trusted frame reads max_cell_temp >=
   *  EIM_BATTERY_OVERHEAT_THRESHOLD_C (serving/app.py) and that reading is part of the
   *  CURRENT, still-running overheat stretch -- same "is the fetched episode still current"
   *  150s-tolerance contract idle_since already has. Independent of `status`: a vehicle can
   *  be overheated while moving, charging, or idle alike. */
  overheat_since: string | null
  /**
   * Raw vendor diagnostic codes -- NOT a severity scale. Confirmed live: vcu_fault_level
   * takes {0,1,2,4} and mcu_error_level takes {0,1,4,5,6,7}, i.e. bitmasks, not booleans
   * or a 0-worst ordering. Shown as-is; nothing in this codebase claims to know which
   * bit(s) mean a real problem without the vendor's own bit reference.
   */
  vcu_fault_level: number | null
  vcu_strategy_fault_level: number | null
  mcu_error_level: number | null
  motor_fault: number | null
}

export interface EimSummary {
  vehicles_reporting: number
  avg_soc: number | null
  idling_now: number
  /** Stationary and drawing charge (batt_cur < -1A) -- excluded from idling_now. JM_BAXI
   *  charges on-site; GTI mostly doesn't (battery swap instead) but the same signal
   *  applies uniformly rather than hardcoding a per-site assumption about who charges. */
  charging_now: number
  /** Stationary, ~0W the whole current stretch -- excluded from idling_now for the same
   *  reason 'parked' is split from 'idle' on EimVehicle (see its own comment). */
  parked_now: number
  idle_hours_today: number
  idle_energy_kwh_today: number
}

export interface EimIdleRow {
  site: EimSite
  vehicle_id: string
  episode_count: number
  idle_hours: number
  idle_energy_kwh: number
  last_idle_ts: string
}

export interface EimTrendRow {
  site: EimSite
  day: string
  idle_hours: number
  idle_energy_kwh: number
  avg_soc: number | null
  distance_km: number | null
}

/**
 * A vehicle idling this long (minutes) or more escalates from routine idle to something
 * that should get noticed -- ground ops explicitly asked for prolonged idle/HVAC draw to
 * be reported, not just shown alongside everything else. One shared bar for both sites.
 * GTI's stationary time is dominated by battery SWAPPING (a genuinely quick stop); a lower
 * bar than that risks flagging a swap-plus-queue as an alert. Charging (either site) is
 * excluded from idle entirely, at a lower layer -- see db/reports_eim.sql -- so it never
 * reaches this threshold check at all, regardless of how long the charge session runs.
 */
export const IDLE_ALERT_THRESHOLD_MIN = 20

/** Works for 'parked' too, not just 'idle' -- how long a stretch has run is informational
 *  either way. Whether that stretch can ever become an ALERT is isIdleAlert's call below,
 *  which deliberately excludes 'parked' regardless of how long it's been. */
export function idleMinutes(v: EimVehicle, now: number): number | null {
  if ((v.status !== 'idle' && v.status !== 'parked') || !v.idle_since) return null
  return Math.max(0, Math.round((now - new Date(v.idle_since).getTime()) / 60_000))
}

/** Never true for 'parked', no matter how long -- a stationary truck drawing ~0W is
 *  switched off, not wastefully idling, and duration alone can't turn "off" into
 *  "needs a look" (see EIM_PARKED_POWER_FLOOR_W's comment in serving/app.py). */
export function isIdleAlert(v: EimVehicle, now: number): boolean {
  if (v.status !== 'idle') return false
  const m = idleMinutes(v, now)
  return m != null && m >= IDLE_ALERT_THRESHOLD_MIN
}

/** kW-at-1-decimal rounds anything under 50W to "0.0", which reads as "no draw" for a
 *  vehicle the backend correctly classified 'idle' over its 10W floor -- confirmed live
 *  at 36.7W. Below 1kW, show watts instead so a real-but-modest draw doesn't look like
 *  the exact zero-draw false alarm the parked/idle split exists to catch. */
export function formatIdlePowerW(w: number): string {
  return w < 1000 ? `${Math.round(w)} W` : `${(w / 1000).toFixed(1)} kW`
}

/**
 * GTI-only: below this SOC a truck risks getting stranded mid-yard rather than reaching
 * a swap station on its own charge. JM_BAXI isn't included -- it charges on-site, so a
 * low reading there is a routine "about to plug in," not the same risk GTI's swap-only
 * model carries.
 */
export const GTI_LOW_SOC_PCT = 25

export function isLowSoc(v: EimVehicle): boolean {
  return v.site === 'gti' && v.soc != null && v.soc < GTI_LOW_SOC_PCT
}

/** Mirrors EIM_BATTERY_OVERHEAT_THRESHOLD_C's sustain requirement -- overheat_since only
 *  ever gets set from a current episode (serving/app.py's _classify_eim_vehicle), but an
 *  episode can be younger than 5 minutes (just crossed, not yet sustained). Same shape as
 *  IDLE_ALERT_THRESHOLD_MIN: duration alone decides whether a live reading escalates to an
 *  alert, not the raw boolean. */
export const BATTERY_OVERHEAT_SUSTAIN_MIN = 5

/** Same 5-minute bound eimStatusKey.ts uses to call a vehicle 'offline'. Shared here because
 *  isBatteryOverheatAlert needs it too: overheat_since is frozen at the episode's last-seen
 *  poll, so without this check a feed that goes silent mid-episode would otherwise keep
 *  accumulating WALL-CLOCK elapsed time against `now` forever -- starting to alert once 5
 *  real-world minutes pass, and never clearing, regardless of how long the vehicle was
 *  actually observed overheated for before it stopped reporting. */
export const EIM_STALE_MS = 5 * 60_000

export function isBatteryOverheatAlert(v: EimVehicle, now: number): boolean {
  if (!v.overheat_since) return false
  if (now - new Date(v.polled_ts).getTime() > EIM_STALE_MS) return false
  return now - new Date(v.overheat_since).getTime() >= BATTERY_OVERHEAT_SUSTAIN_MIN * 60_000
}

/** Mirrors idleMinutes' own shape -- how long the CURRENT overheat stretch has run,
 *  independent of whether it has crossed the alert threshold yet (isBatteryOverheatAlert's
 *  call). null when there is no current overheat episode to measure. */
export function overheatMinutes(v: EimVehicle, now: number): number | null {
  if (!v.overheat_since) return null
  // Math.floor, not round -- the 5-minute sustain bar is tight enough (vs. idle's 20m)
  // that rounding up would show "5m" a full 30s before isBatteryOverheatAlert agrees.
  return Math.max(0, Math.floor((now - new Date(v.overheat_since).getTime()) / 60_000))
}

async function eimFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const r = await fetch(path, {
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json', ...init?.headers },
    ...init,
  })
  const body = await r.json().catch(() => ({}))
  if (!r.ok) {
    const msg = (body as { error?: string }).error ?? `Request failed (${r.status})`
    if (r.status === 403) notifyForbidden(msg)
    throw new Error(msg)
  }
  return body as T
}

function siteQuery(site: EimSite | 'all'): string {
  return site === 'all' ? '' : `?site=${site}`
}

export async function fetchEimLive(site: EimSite | 'all'): Promise<EimVehicle[]> {
  const data = await eimFetch<{ rows?: EimVehicle[] }>(`/api/eim/live${siteQuery(site)}`)
  return data.rows ?? []
}

export async function fetchEimSummary(site: EimSite | 'all'): Promise<EimSummary> {
  return eimFetch<EimSummary>(`/api/eim/summary${siteQuery(site)}`)
}

export async function fetchEimIdleEpisodes(site: EimSite | 'all'): Promise<EimIdleRow[]> {
  const data = await eimFetch<{ rows?: EimIdleRow[] }>(`/api/eim/idle-episodes${siteQuery(site)}`)
  return data.rows ?? []
}

export async function fetchEimTrend(site: EimSite | 'all', days = 14): Promise<EimTrendRow[]> {
  const q = site === 'all' ? `?days=${days}` : `?site=${site}&days=${days}`
  const data = await eimFetch<{ rows?: EimTrendRow[] }>(`/api/eim/trend${q}`)
  return data.rows ?? []
}

// ---------------------------------------------------------------- breakdowns
/** Same site_breakdown-backed shape as StarBreakdown (console/src/star/useStarApi.ts),
 *  through serving/app.py's generalized /api/eim/{site}/breakdowns -- one function
 *  family shared with STAR, not a second implementation. Ports still gets its own
 *  fetch helpers here because a breakdown always belongs to exactly one of GTI/JM_BAXI
 *  (there is no site-agnostic write path), while reads can combine both -- see
 *  fetchEimBreakdowns' own 'all' handling below. */
export interface EimBreakdown {
  site: EimSite
  breakdown_id: number
  vehicle_id: string
  category: string
  severity: string
  note: string | null
  contact: string | null
  by_whom: string | null
  opened_ts: number | null
  cleared_ts: number | null
  eta_min: number | null
  lat: number | null
  lon: number | null
  /** Telemetry's own suggestion -- a poll against eim_vehicle_telemetry (Phase 1's
   *  _resumed_moving_poll_tick_ports), not a live frame the way Ultratech's is, but the
   *  same contract: a prompt for a human to confirm, never a silent auto-clear. */
  resumed_moving_ts: number | null
  resumed_moving_lat: number | null
  resumed_moving_lon: number | null
}

export interface EimBreakdownAction {
  action: string
  by: string | null
  phone: string | null
  note: string | null
  ts: number | null
}

export interface EimBreakdownCode {
  code: string
  name: string
}

/** 'all' fetches both sites in parallel and merges -- there is no combined-site read
 *  endpoint on the backend (a breakdown is always single-site), so this is the one place
 *  that reconciles the console's All sites | GTI | JM Baxi switcher with a CRUD surface
 *  that's deliberately per-site only. */
export async function fetchEimBreakdowns(site: EimSite | 'all', openOnly = true): Promise<EimBreakdown[]> {
  const q = openOnly ? '' : '?open_only=false'
  const sites: EimSite[] = site === 'all' ? ['gti', 'jm_baxi'] : [site]
  const results = await Promise.all(sites.map((s) =>
    eimFetch<{ breakdowns: EimBreakdown[] }>(`/api/eim/${s}/breakdowns${q}`)))
  return results.flatMap((r) => r.breakdowns ?? [])
}

export async function fetchEimBreakdownCodes(site: EimSite): Promise<EimBreakdownCode[]> {
  const data = await eimFetch<{ codes: EimBreakdownCode[] }>(`/api/eim/${site}/breakdowns/codes`)
  return data.codes ?? []
}

export async function openEimBreakdown(site: EimSite, body: {
  vehicle_id: string
  category?: string
  severity?: string
  note?: string
  contact?: string
  by?: string
  phone?: string
  eta_min?: number | null
  lat?: number | null
  lon?: number | null
}): Promise<{ breakdown_id: number; reused?: boolean }> {
  return eimFetch(`/api/eim/${site}/breakdowns`, { method: 'POST', body: JSON.stringify(body) })
}

export async function actOnEimBreakdown(
  site: EimSite,
  id: number,
  action: string,
  extra: { by?: string; phone?: string; note?: string } = {},
): Promise<void> {
  await eimFetch(`/api/eim/${site}/breakdowns/${id}/actions`, {
    method: 'POST',
    body: JSON.stringify({ action, ...extra }),
  })
}

/** Clears the "resumed moving" suggestion without resolving anything -- for the
 *  false-positive case, same semantics as Ultratech's and STAR's own dismiss-moving. */
export async function dismissEimMoving(site: EimSite, id: number): Promise<void> {
  await eimFetch(`/api/eim/${site}/breakdowns/${id}/actions`, {
    method: 'POST',
    body: JSON.stringify({ action: 'dismiss-moving' }),
  })
}

export async function fetchEimBreakdownActionLog(site: EimSite, id: number): Promise<EimBreakdownAction[]> {
  const data = await eimFetch<{ actions: EimBreakdownAction[] }>(`/api/eim/${site}/breakdowns/${id}/actions`)
  return data.actions ?? []
}

// ---------------------------------------------------------------- day-wise breakdown reports
export type EimBreakdownReportKind = 'daily' | 'downtime' | 'repeat' | 'open' | 'hygiene'

export interface EimBreakdownDailyRow {
  site: EimSite; day: string; opened: number; cleared: number; updated: number
  vehicles_down: number; immobilised: number; major: number; minor: number
}

export interface EimBreakdownDowntimeRow {
  site: EimSite; category: string; severity: string; incidents: number; still_open: number
  vehicles: number; median_hours: number | null; p90_hours: number | null
  total_hours_lost: number | null
}

export interface EimBreakdownRepeatRow {
  site: EimSite; vehicle_id: string; depot: string | null; incidents: number
  distinct_causes: number; most_common_cause: string | null; hours_lost: number | null
  first_incident: number; last_incident: number
}

export interface EimBreakdownOpenRow {
  site: EimSite; breakdown_id: number; vehicle_id: string; depot: string | null
  category: string; severity: string; note: string | null; contact: string | null
  by_whom: string | null; eta_min: number | null; opened_ts: number; hours_down: number
  eta_overdue: boolean; resumed_moving_ts: number | null
}

export interface EimBreakdownHygieneRow {
  site: EimSite; incidents_logged: number; never_cleared: number; open_over_7_days: number
  never_cleared_pct: number | null; currently_marked_down: number
}

export type EimBreakdownReportRow<K extends EimBreakdownReportKind> =
  K extends 'daily' ? EimBreakdownDailyRow :
  K extends 'downtime' ? EimBreakdownDowntimeRow :
  K extends 'repeat' ? EimBreakdownRepeatRow :
  K extends 'open' ? EimBreakdownOpenRow :
  EimBreakdownHygieneRow

/** site omitted (or 'all') combines both ports -- this one DOES have a combined-site
 *  backend endpoint (/api/eim/breakdowns/reports/{kind}, no {site} path segment),
 *  unlike the CRUD routes above, since a report is read-only aggregation and the server
 *  can do the UNION itself instead of the client merging two round trips. */
export async function fetchEimBreakdownReport<K extends EimBreakdownReportKind>(
  kind: K, site: EimSite | 'all' = 'all',
): Promise<EimBreakdownReportRow<K>[]> {
  const q = site === 'all' ? '' : `?site=${site}`
  const data = await eimFetch<{ rows?: EimBreakdownReportRow<K>[] }>(
    `/api/eim/breakdowns/reports/${kind}${q}`)
  return data.rows ?? []
}

// ---------------------------------------------------------------- idle-alert history
export type EimIdleAlertReportKind = 'daily' | 'vehicle'

export interface EimIdleAlertDailyRow {
  site: EimSite; day: string; episode_count: number
  total_hours: number; total_energy_kwh: number
}

export interface EimIdleAlertVehicleRow {
  site: EimSite; vehicle_id: string; episode_count: number
  total_hours: number; total_energy_kwh: number; last_episode_ts: number
}

export interface EimIdleAlertListRow {
  site: EimSite; vehicle_id: string
  episode_started_ts: number; alert_triggered_ts: number; episode_ended_ts: number
  duration_sec: number; avg_power_w: number; idle_energy_kwh: number
}

export type EimIdleAlertReportRow<K extends EimIdleAlertReportKind> =
  K extends 'daily' ? EimIdleAlertDailyRow : EimIdleAlertVehicleRow

/** opts.days is the trailing window (?days=N). Omitted means all time -- which over
 *  eim_idle_alert_episode, a table that is never purged, is a full scan that grows
 *  forever, so callers should normally pass one. */
export async function fetchEimIdleAlertReport<K extends EimIdleAlertReportKind>(
  kind: K, site: EimSite | 'all' = 'all', opts: { days?: number } = {},
): Promise<EimIdleAlertReportRow<K>[]> {
  const params = new URLSearchParams()
  if (site !== 'all') params.set('site', site)
  if (opts.days != null) params.set('days', String(opts.days))
  const q = params.toString()
  const data = await eimFetch<{ rows?: EimIdleAlertReportRow<K>[] }>(
    `/api/eim/idle-alerts/reports/${kind}${q ? `?${q}` : ''}`)
  return data.rows ?? []
}

export async function fetchEimIdleAlertList(
  site: EimSite | 'all' = 'all',
  opts: { vehicleId?: string; fromTs?: string; toTs?: string; limit?: number; offset?: number } = {},
): Promise<EimIdleAlertListRow[]> {
  const params = new URLSearchParams()
  if (site !== 'all') params.set('site', site)
  if (opts.vehicleId) params.set('vehicle_id', opts.vehicleId)
  if (opts.fromTs) params.set('from_ts', opts.fromTs)
  if (opts.toTs) params.set('to_ts', opts.toTs)
  params.set('limit', String(opts.limit ?? 100))
  params.set('offset', String(opts.offset ?? 0))
  const data = await eimFetch<{ rows?: EimIdleAlertListRow[] }>(
    `/api/eim/idle-alerts/reports/list?${params.toString()}`)
  return data.rows ?? []
}

// ---------------------------------------------------------------- BATTERY_OVERHEAT alert history
// Structurally identical to the idle-alert history section just above -- same
// eim_overheat_alert_episode table (db/schema_eim.sql), same
// /api/eim/overheat-alerts/reports/{kind} route shape (serving/app.py).
export type EimOverheatAlertReportKind = 'daily' | 'vehicle'

export interface EimOverheatAlertDailyRow {
  site: EimSite; day: string; episode_count: number
  total_hours: number; max_temp_c: number
}

export interface EimOverheatAlertVehicleRow {
  site: EimSite; vehicle_id: string; episode_count: number
  total_hours: number; max_temp_c: number; last_episode_ts: number
}

export interface EimOverheatAlertListRow {
  site: EimSite; vehicle_id: string
  episode_started_ts: number; alert_triggered_ts: number; episode_ended_ts: number
  duration_sec: number; max_temp_c: number
  /** 'charging' | 'moving' | 'idle' | null -- null when no telemetry row survived to
   *  archive time (f_eim_archive_overheat_alerts, db/reports_eim.sql). */
  vehicle_status: string | null
}

export type EimOverheatAlertReportRow<K extends EimOverheatAlertReportKind> =
  K extends 'daily' ? EimOverheatAlertDailyRow : EimOverheatAlertVehicleRow

/** opts.days is the trailing window (?days=N). Omitted means all time -- which over
 *  eim_overheat_alert_episode, a table that is never purged, is a full scan that grows
 *  forever, so callers should normally pass one. */
export async function fetchEimOverheatAlertReport<K extends EimOverheatAlertReportKind>(
  kind: K, site: EimSite | 'all' = 'all', opts: { days?: number } = {},
): Promise<EimOverheatAlertReportRow<K>[]> {
  const params = new URLSearchParams()
  if (site !== 'all') params.set('site', site)
  if (opts.days != null) params.set('days', String(opts.days))
  const q = params.toString()
  const data = await eimFetch<{ rows?: EimOverheatAlertReportRow<K>[] }>(
    `/api/eim/overheat-alerts/reports/${kind}${q ? `?${q}` : ''}`)
  return data.rows ?? []
}

export async function fetchEimOverheatAlertList(
  site: EimSite | 'all' = 'all',
  opts: { vehicleId?: string; fromTs?: string; toTs?: string; limit?: number; offset?: number } = {},
): Promise<EimOverheatAlertListRow[]> {
  const params = new URLSearchParams()
  if (site !== 'all') params.set('site', site)
  if (opts.vehicleId) params.set('vehicle_id', opts.vehicleId)
  if (opts.fromTs) params.set('from_ts', opts.fromTs)
  if (opts.toTs) params.set('to_ts', opts.toTs)
  params.set('limit', String(opts.limit ?? 100))
  params.set('offset', String(opts.offset ?? 0))
  const data = await eimFetch<{ rows?: EimOverheatAlertListRow[] }>(
    `/api/eim/overheat-alerts/reports/list?${params.toString()}`)
  return data.rows ?? []
}

// ---------------------------------------------------------------- real-time overheat lifecycle (SPEC.md 2.4/2.5)
/** One BATTERY_OVERHEAT episode, real-time -- open (cleared_ts null) or cleared earlier
 *  today. Timestamp fields accept either an ISO string or epoch-ms number -- the backend
 *  route's own encoding is deliberately not re-litigated here (see SPEC.md's own
 *  `_ts_matches` note); callers pass the raw value into `new Date(...)`, which accepts
 *  both. */
export interface EimOverheatEpisode {
  episode_started_ts: string | number
  episode_ended_ts: string | number
  cleared_ts: string | number | null
  max_temp_c: number
  /** null for a pre-deploy legacy episode, or if the Open-Meteo lookup failed -- never
   *  a fabricated value. */
  ambient_temp_c: number | null
  ambient_temp_src: 'vehicle' | null
  vehicle_status: string | null
  duration_sec: number
}

export interface EimOverheatSample {
  polled_ts: string | number
  max_cell_temp: number
}

/** Today's (IST calendar day) BATTERY_OVERHEAT episodes for one vehicle, open or
 *  cleared -- backs the alert panel's triggered/cleared columns and the detail card,
 *  so a cleared episode keeps showing for the rest of the day instead of vanishing the
 *  moment the live tile stops flagging it. */
export async function fetchEimOverheatEpisodesToday(site: EimSite, vehicleId: string): Promise<EimOverheatEpisode[]> {
  const data = await eimFetch<{ episodes?: EimOverheatEpisode[] }>(
    `/api/eim/${site}/vehicles/${encodeURIComponent(vehicleId)}/overheat-episodes`)
  return data.episodes ?? []
}

export interface EimOverheatEpisodeForVehicle extends EimOverheatEpisode {
  site: EimSite
  vehicle_id: string
}

/** Fleet-level sibling of fetchEimOverheatEpisodesToday -- every vehicle at a site in
 *  one request, meant to ride the console's existing 10s refresh() cycle (EimShell.tsx)
 *  rather than one request per vehicle (adversarial review, HIGH: the original
 *  per-vehicle-fetch design fired ~66 parallel requests on page load for a ~66-vehicle
 *  Ports fleet, against this worktree's own CLAUDE.md "no new recurring poll loop" rule
 *  and the documented 70-request/503 incident threshold). */
export async function fetchEimOverheatEpisodesTodaySite(site: EimSite): Promise<EimOverheatEpisodeForVehicle[]> {
  const data = await eimFetch<{ episodes?: EimOverheatEpisodeForVehicle[] }>(
    `/api/eim/${site}/overheat-episodes-today`)
  return data.episodes ?? []
}

/** One episode's per-poll cell-temperature series, ordered oldest-first -- powers the
 *  detail card's "View trend" chart. Rejects (via eimFetch) on a 404 -- an unknown
 *  episode is a caller error, not an empty-but-valid result. */
export async function fetchEimOverheatTrend(
  site: EimSite, vehicleId: string, episodeStartedTs: string,
): Promise<EimOverheatSample[]> {
  const data = await eimFetch<{ samples?: EimOverheatSample[] }>(
    `/api/eim/${site}/vehicles/${encodeURIComponent(vehicleId)}/overheat-trend`
    + `?episode_started_ts=${encodeURIComponent(episodeStartedTs)}`)
  return data.samples ?? []
}

// ---------------------------------------------------------------- alert acknowledgement
/** IDLE_ALERT, LOW_SOC and BATTERY_OVERHEAT are the only alert kinds this console computes
 *  for Ports (isIdleAlert/isLowSoc/isBatteryOverheatAlert above) -- ack is scoped to exactly
 *  those, not an open string. */
export type EimAlertKind = 'IDLE_ALERT' | 'LOW_SOC' | 'BATTERY_OVERHEAT'

export interface EimAlertAck {
  vehicle_id: string
  kind: EimAlertKind
  /** '' for LOW_SOC (no natural episode boundary) -- idle_since (ISO) for IDLE_ALERT,
   *  overheat_since (ISO) for BATTERY_OVERHEAT, so a new idle/overheat stretch always
   *  needs a fresh acknowledgement. See eim_alert_ack's own comment in db/schema_eim.sql
   *  for why LOW_SOC differs here. */
  episode: string
  by_whom: string
  acked_ts: number
}

/** 'all' fetches both sites in parallel and merges -- same shape as fetchEimBreakdowns'
 *  own 'all' handling, for the same reason (no combined-site read endpoint; a write is
 *  always single-site, so the server never needs to union these itself). */
export async function fetchEimAlertAcks(site: EimSite | 'all'): Promise<EimAlertAck[]> {
  const sites: EimSite[] = site === 'all' ? ['gti', 'jm_baxi'] : [site]
  const results = await Promise.all(sites.map((s) =>
    eimFetch<{ acks: EimAlertAck[] }>(`/api/eim/${s}/alerts/acks`)))
  return results.flatMap((r) => r.acks ?? [])
}

export async function ackEimAlert(
  site: EimSite, vehicleId: string, kind: EimAlertKind, episode: string, by: string,
): Promise<void> {
  await eimFetch(`/api/eim/${site}/alerts/ack`, {
    method: 'POST',
    body: JSON.stringify({ vehicle_id: vehicleId, kind, episode, by }),
  })
}

/** Clears an acknowledgement -- for the mis-click case, same "undo, don't hide" shape
 *  every dismiss action in this console already has (dismissEimMoving above,
 *  dismissStarMoving, Ultratech's own reopen). */
export async function unackEimAlert(
  site: EimSite, vehicleId: string, kind: EimAlertKind, episode: string, by: string,
): Promise<void> {
  await eimFetch(`/api/eim/${site}/alerts/unack`, {
    method: 'POST',
    body: JSON.stringify({ vehicle_id: vehicleId, kind, episode, by }),
  })
}
